/**
 * GPS Location Adapter
 * Provides backward compatibility with old LocationContext interface
 * Adapts new GPS services data to old LocationState format
 */

import { rawGpsDataService, RawGpsDataService } from "../services/navigation/RawGpsDataService";

/**
 * Location state interface (compatible with old LocationContext)
 */
export interface LocationState {

    /** GPS accuracy in meters */
    "accuracy": number;

    /** Altitude in meters above sea level */
    "altitude": number | null;

    /** Altitude accuracy in meters */
    "altitudeAccuracy": number | null;

    /** GPS heading in degrees (0-360) - from device GPS sensor, not compass */
    "heading": number | null;

    /** Latitude coordinate in decimal degrees */
    "latitude": number;

    /** Longitude coordinate in decimal degrees */
    "longitude": number;

    /** Speed in meters per second */
    "speed": number | null;

    /** Timestamp when location was captured */
    "timestamp": number;
}

/** Default location (Geneva, Switzerland) */
const defaultLocation: LocationState = {
    "accuracy":         0,
    "altitude":         null,
    "altitudeAccuracy": null,
    "heading":          null,
    "latitude":         46.201746,
    "longitude":        6.140708,
    "speed":            null,
    "timestamp":        Date.now(),
};

/**
 * Get current user location from GPS service
 * @returns Current location state or default if not available
 */
export function getUserLocation(): LocationState {
    const gpsData = rawGpsDataService.getLastValue();

    if ( gpsData === null ) {
        return defaultLocation;
    }

    return {
        "accuracy":         gpsData.accuracy,
        "altitude":         gpsData.altitude,
        "altitudeAccuracy": gpsData.altitudeAccuracy,
        "heading":          gpsData.heading,
        "latitude":         gpsData.latitude,
        "longitude":        gpsData.longitude,
        "speed":            gpsData.speed,
        "timestamp":        gpsData.timestamp,
    };
}

/**
 * Check if GPS location is available
 * @returns True if GPS has valid data
 */
export function hasGpsLocation(): boolean {
    return rawGpsDataService.getLastValue() !== null;
}

/**
 * Subscribe to GPS location updates
 * @param callback - Callback function with location state
 * @returns Unsubscribe function
 */
export function subscribeToLocation( callback: ( location: LocationState ) => void ): () => void {
    const rawGpsService = RawGpsDataService.getInstance();

    return rawGpsService.subscribe( ( gpsData ) => {
        callback( {
            "accuracy":         gpsData.accuracy,
            "altitude":         gpsData.altitude,
            "altitudeAccuracy": gpsData.altitudeAccuracy,
            "heading":          gpsData.heading,
            "latitude":         gpsData.latitude,
            "longitude":        gpsData.longitude,
            "speed":            gpsData.speed,
            "timestamp":        gpsData.timestamp,
        } );
    } );
}

